import React, { FC, useState, useEffect, useRef } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Button, InputGroup, ReactSelectOverrides } from '@bphxd/ds-core-react';
import { EMPTY_ISOLATION } from '../../../lib/constants';
import { TIsolation } from '../../../lib/types';
import { GET_ECOW_ISOLATION_BY_PERMIT_NUMBER } from '../../../graph-queries/tar/isolation';
import { useLazyQuery } from '@apollo/client';
import { useSelectedAsset } from '../../../lib/util';
import Select from 'react-select';
import { renderOptions } from '../../../lib/util/render';
import {
  ISOLATION_DRAWING_STATUS_ENUM,
  ISOLATION_LINE_WALK_PACK_STATUS_ENUM,
  ISOLATION_SOURCE_ENUM,
} from '../../../lib/constants/dbConstants';
import {
  IsolationDrawingStatusEnum,
  IsolationLineWalkPackStatusEnum,
  IsolationSourceEnum,
} from '../../../graph-queries/__generated__/graphql';

type TOption = { label: string; value: string };
export const REQUIRED_TRADE_OPTIONS: TOption[] = ['Process', 'Electric', 'Instrument'].map((value) => ({
  value,
  label: value,
}));

const IsolationForm: FC<{
  defaultValues?: TIsolation;
  onSubmit: (isolation: TIsolation) => Promise<unknown>;
  onClose: () => void;
  type: 'add' | 'edit';
  isLoading?: boolean;
}> = ({ defaultValues, onSubmit, type, onClose, isLoading }) => {
  const [getEcowIsolation, { data, loading, error }] = useLazyQuery(
    GET_ECOW_ISOLATION_BY_PERMIT_NUMBER
  );
  const asset = useSelectedAsset();

  const showData = !loading && !error && data;
  const [isolationNumber, setIsolationNumber] = useState(defaultValues?.isolationNumber || '');
  const [inputIsolationNumber, setInputIsolationNumber] = useState(
    defaultValues?.isolationNumber || ''
  );
  //Logic for setting the comment field to autosize height
  const [commentHeight, setCommentHeight] = useState('auto');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustTextAreaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      setCommentHeight(`${textarea.scrollHeight}px`);
    }
  };

  useEffect(() => {
    adjustTextAreaHeight();
  }, [defaultValues?.comments]); // Re-run if `defaultValues.comments` changes

  const handleInput = (event: { target: { style: { height: string }; scrollHeight: any } }) => {
    adjustTextAreaHeight();
  };

  const submitIsolationNumber = (isolationNumber: string) => {
    getEcowIsolation({ variables: { permitNumber: isolationNumber } });
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();

    const data = new FormData(event.target as HTMLFormElement);
    const inputData = Object.fromEntries(data) as Record<string, string> & {
      drawingStatus: IsolationDrawingStatusEnum;
      source: IsolationSourceEnum;
      lineWalkPackStatus: IsolationLineWalkPackStatusEnum;
    };

    const isolation: TIsolation = {
      ...EMPTY_ISOLATION,
      ...inputData,
      assetId: asset?.assetId || -1,
      fieldWalked: inputData.fieldWalked === 'Yes',
      totalHours: parseInt(inputData.totalHours),
      packNumber: parseInt(inputData.packNumber),
      requiredTrades: data
        .getAll('requiredTrades')
        .map((trade) => trade.toString())
        .join(','),
    };
    if (defaultValues?.isolationId) isolation.isolationId = defaultValues.isolationId;

    onSubmit(isolation)
      .then(onClose)
      .catch((e) => {
        console.error(e);
      });
  };

  const requiredTrades: TOption[] = defaultValues?.requiredTrades
    ? defaultValues.requiredTrades.split(',').map((value) => ({ value, label: value }))
    : [];

  return (
    <Form className="row g-3 px-5" onSubmit={handleSubmit}>
      <div className="col-md-6">
        <FormGroup>
          <Label for="losh-number">LOSH Number</Label>
          <Input
            type="text"
            name="loshNumber"
            id="losh-number"
            required
            defaultValue={defaultValues?.loshNumber || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="isolation-description">Description</Label>
          <Input
            type="text"
            name="title"
            id="isolation-description"
            required
            defaultValue={defaultValues?.title || ''}
          />
        </FormGroup>
      </div>

      <div className="col-md-6">
        <FormGroup>
          <Label for="drawing-status">Drawing Status</Label>
          <Input
            type="select"
            name="drawingStatus"
            id="drawing-status"
            className="form-select-placeholder-hack"
            required
            role="combobox"
            defaultValue={defaultValues?.drawingStatus || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(ISOLATION_DRAWING_STATUS_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="field-walked">Field Walked</Label>
          <Input
            type="select"
            name="fieldWalked"
            id="field-walked"
            className="form-select-placeholder-hack"
            required
            role="combobox"
            defaultValue={
              defaultValues?.fieldWalked === true
                ? 'Yes'
                : defaultValues?.fieldWalked === false
                  ? 'No'
                  : ''
            }
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            <option>Yes</option>
            <option>No</option>
          </Input>
        </FormGroup>
      </div>

      <div className="col-md-12">
        <FormGroup>
          <Label for="required-trades">Required Trades</Label>
          <Select<{ value: string; label: string }, true>
            name="requiredTrades"
            id="required-trades"
            isMulti
            defaultValue={requiredTrades}
            options={REQUIRED_TRADE_OPTIONS}
            components={{
              ...ReactSelectOverrides.default,
              IndicatorSeparator: ReactSelectOverrides.NoIndicatorSeparator,
            }}
            placeholder="Choose option(s)"
            classNamePrefix="react-select"
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="isolation-source">Electrical Source</Label>
          <Input
            type="select"
            name="source"
            id="isolation-source"
            required
            role="combobox"
            defaultValue={defaultValues?.source || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(ISOLATION_SOURCE_ENUM)}
          </Input>
        </FormGroup>
      </div>

      <div className="col-md-12">
        <FormGroup>
          <Label for="isolation-number">Isolation Number</Label>
          <InputGroup className="mb-3">
            <Input
              placeholder="Search eCOW"
              type="text"
              name="isolationNumber"
              id="isolation-number"
              value={inputIsolationNumber}
              onChange={(e) => {
                setInputIsolationNumber((e.target as HTMLInputElement).value);
              }}
            />
            <Button
              disabled={!inputIsolationNumber}
              color="darker-outline"
              onClick={(e) => submitIsolationNumber(inputIsolationNumber)}
            >
              Search
            </Button>
          </InputGroup>
        </FormGroup>
      </div>

      <div className="col-md-12">
        <FormGroup className="d-flex justify-content-between">
          <Label className="fw-medium">IDP Status</Label>
          <Label>{showData ? data?.ecowIsolation.statusComment : '-'}</Label>
        </FormGroup>
        {showData && (
          <>
            <Label className="fw-medium">Isolation Points</Label>
            <FormGroup className="d-flex justify-content-between">
              <Label>Process</Label>
              <Label>{data?.ecowIsolation.processIsolaitonPoints}</Label>
            </FormGroup>
            <FormGroup className="d-flex justify-content-between">
              <Label>Electrical LV1</Label>
              <Label>{data?.ecowIsolation.elecLV1IsolationPoints}</Label>
            </FormGroup>
            <FormGroup className="d-flex justify-content-between">
              <Label>Instrument</Label>
              <Label>{data?.ecowIsolation.instrumentIsolationPoints}</Label>
            </FormGroup>
            <FormGroup className="d-flex justify-content-between">
              <Label>Electrical LV2</Label>
              <Label>{data?.ecowIsolation.elecLV2IsolationPoints}</Label>
            </FormGroup>
            <FormGroup className="d-flex justify-content-between">
              <Label>Electrical HV</Label>
              <Label>{data?.ecowIsolation.elecHVIsolationPoints}</Label>
            </FormGroup>
          </>
        )}
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="pack-number">Line Walk Pack Number</Label>
          <Input
            type="number"
            name="packNumber"
            id="pack-number"
            defaultValue={defaultValues?.packNumber || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="walkpack-status">Line Walk Pack Status</Label>
          <Input
            type="select"
            name="lineWalkPackStatus"
            id="walkpack-status"
            className="form-select-placeholder-hack"
            required
            role="combobox"
            defaultValue={defaultValues?.lineWalkPackStatus || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(ISOLATION_LINE_WALK_PACK_STATUS_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="isolation-comments" className="label-optional">
            Comments
          </Label>
          <Input
            type="textarea"
            innerRef={textareaRef}
            style={{ height: commentHeight }}
            onChange={handleInput}
            name="comments"
            id="isolation-comments"
            defaultValue={defaultValues?.comments || ''}
          />
        </FormGroup>
      </div>
      {type === 'add' ? (
        <div className="col-md-12">
          <Button
            type="submit"
            size="lg"
            className="w-100 my-4"
            theme="darker"
            level="primary"
            disabled={isLoading}
          >
            Add new isolation
          </Button>
        </div>
      ) : (
        <>
          <div className="col-md-6">
            <Button
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="tertiary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
          <div className="col-md-6">
            <Button
              type="submit"
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="primary"
              disabled={isLoading}
            >
              Save changes
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};

export default IsolationForm;
